/**
 * @file constants.ts
 * @description Shared constants for the MedRCM healthcare platform.
 *
 * This file contains all application constants that are referenced across
 * frontend, backend, and mobile. Changes here propagate everywhere via imports.
 *
 * Categories:
 * - HIPAA compliance settings
 * - Session and security timeouts
 * - Risk level color scheme
 * - Appointment durations
 * - Vital sign normal ranges
 * - Cookie names
 * - Error messages
 */

// ============================================================================
// HIPAA COMPLIANCE
// ============================================================================

/**
 * HIPAA idle session timeout (minutes).
 * Sessions expire after 15 minutes of inactivity per HIPAA Security Rule.
 */
export const HIPAA_SESSION_TIMEOUT_MINUTES = 15;

/**
 * HIPAA maximum session lifetime (hours).
 * Even with activity, sessions expire after 8 hours per privacy rule.
 */
export const HIPAA_MAX_SESSION_LIFETIME_HOURS = 8;

/**
 * HIPAA breach notification threshold (number of records).
 * If more than this number of patient records are affected by a breach, must notify HHS.
 */
export const HIPAA_BREACH_NOTIFICATION_THRESHOLD = 500;

/**
 * HIPAA access log retention (days).
 * Access logs must be retained for at least 6 years per HIPAA Audit Controls.
 * We retain 7 years for safety margin.
 */
export const HIPAA_ACCESS_LOG_RETENTION_DAYS = 365 * 7;

/**
 * HIPAA encryption algorithm for PHI at rest.
 * AES-256-GCM minimum.
 */
export const HIPAA_ENCRYPTION_ALGORITHM = "aes-256-gcm";

/**
 * HIPAA minimum password requirements.
 */
export const HIPAA_PASSWORD_REQUIREMENTS = {
  /** Minimum length in characters */
  minLength: 12,

  /** Must include uppercase letter? */
  requireUppercase: true,

  /** Must include number? */
  requireNumber: true,

  /** Must include special character? */
  requireSpecialChar: true,

  /** Password expiry period (days) */
  expiryDays: 90,
};

/**
 * Confidentiality levels for sensitive patient data.
 */
export const CONFIDENTIALITY_LEVELS = {
  /** Standard records: allergies, chronic conditions, lab results */
  NORMAL: "normal",

  /** Sensitive conditions requiring heightened access controls: HIV, psychiatric, substance abuse */
  RESTRICTED: "restricted",

  /** Rare, marked by explicit patient consent: genetic data, experimental treatments */
  HIGHLY_RESTRICTED: "highly_restricted",
} as const;

// ============================================================================
// SESSION & SECURITY TIMEOUTS
// ============================================================================

/**
 * Default session timeout (minutes) if not HIPAA-regulated.
 */
export const DEFAULT_SESSION_TIMEOUT_MINUTES = 30;

/**
 * Activity check interval (seconds).
 * Frontend checks if user is idle at this interval.
 */
export const ACTIVITY_CHECK_INTERVAL_SECONDS = 60;

/**
 * Execution token TTL (minutes).
 * Tokens granted for high/critical mutations expire after this period.
 */
export const EXECUTION_TOKEN_TTL_MINUTES = 30;

/**
 * Approval request TTL (hours).
 * Approval requests for high/critical mutations expire after this period.
 */
export const APPROVAL_REQUEST_TTL_HOURS = 1;

/**
 * Password reset token TTL (hours).
 */
export const PASSWORD_RESET_TOKEN_TTL_HOURS = 1;

/**
 * Email verification token TTL (hours).
 */
export const EMAIL_VERIFICATION_TOKEN_TTL_HOURS = 24;

/**
 * OTP (one-time password) validity (minutes).
 * Used for multi-factor authentication.
 */
export const OTP_VALIDITY_MINUTES = 10;

/**
 * OTP maximum attempts before lockout.
 */
export const OTP_MAX_ATTEMPTS = 3;

/**
 * OTP lockout duration (minutes) after max attempts.
 */
export const OTP_LOCKOUT_DURATION_MINUTES = 15;

// ============================================================================
// GOVERNANCE & RISK LEVELS
// ============================================================================

/**
 * Risk level color scheme for admin dashboards.
 * Used to visually highlight capability severity.
 */
export const RISK_LEVEL_COLORS = {
  /** Critical risk: Always requires approval by owner */
  critical: "#DC2626", // red-600

  /** High risk: Requires approval by designated approver */
  high: "#EA580C", // orange-600

  /** Medium risk: Soft escalation, auto-allow with evidence recording */
  medium: "#FBBF24", // amber-400

  /** Low risk: Auto-allow with evidence recording */
  low: "#10B981", // emerald-600
} as const;

/**
 * Risk level labels.
 */
export const RISK_LEVEL_LABELS = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
} as const;

/**
 * Capability domains (governance categories).
 */
export const CAPABILITY_DOMAINS = {
  /** Prescription creation, modification, cancellation */
  PRESCRIPTION: "prescription",

  /** Diagnosis recording, modification */
  DIAGNOSIS: "diagnosis",

  /** Patient record access, export, sharing */
  PATIENT: "patient",

  /** Billing operations, invoice creation, payment processing */
  BILLING: "billing",

  /** Clinical data modification (vital signs, lab results) */
  CLINICAL: "clinical",

  /** User management, role assignment */
  USER_MANAGEMENT: "user_management",

  /** System administration, configuration */
  SYSTEM: "system",

  /** AI-generated content, recommendations */
  AI: "ai",
} as const;

/**
 * Execution paths (how a capability was evaluated).
 */
export const EXECUTION_PATHS = {
  /** Token validation: Required token provided and valid */
  TOKEN_VALIDATED: "token-validated",

  /** MedRCM SDK: External policy engine consulted */
  MEDRCM_SDK: "medrcm-sdk",

  /** Local policy: Embedded kernel policy evaluation */
  LOCAL_POLICY: "local-policy",
} as const;

// ============================================================================
// APPOINTMENT MANAGEMENT
// ============================================================================

/**
 * Standard appointment durations (minutes).
 */
export const APPOINTMENT_DURATIONS = {
  /** General consultation or follow-up */
  STANDARD: 30,

  /** Extended consultation */
  EXTENDED: 60,

  /** Brief check-in or triage */
  BRIEF: 15,

  /** Procedure or comprehensive exam */
  PROCEDURE: 90,
} as const;

/**
 * Appointment cancellation notice period (hours).
 * Appointments must be cancelled this many hours in advance.
 */
export const APPOINTMENT_CANCELLATION_NOTICE_HOURS = 24;

/**
 * Appointment reminder timing (minutes before appointment).
 */
export const APPOINTMENT_REMINDER_MINUTES = [60, 15];

/**
 * Telehealth session timeout (minutes).
 * If no activity detected, session is ended.
 */
export const TELEHEALTH_SESSION_TIMEOUT_MINUTES = 60;

// ============================================================================
// VITAL SIGNS NORMAL RANGES
// ============================================================================

/**
 * Normal vital sign ranges for adults.
 * Used for flagging abnormal results in clinical assessments.
 */
export const VITAL_SIGN_RANGES = {
  /** Temperature in Fahrenheit */
  temperatureFahrenheit: {
    min: 97.0,
    max: 99.0,
  },

  /** Systolic blood pressure (mmHg) */
  systolicBP: {
    min: 90,
    max: 120,
  },

  /** Diastolic blood pressure (mmHg) */
  diastolicBP: {
    min: 60,
    max: 80,
  },

  /** Heart rate (beats per minute) */
  heartRate: {
    min: 60,
    max: 100,
  },

  /** Respiratory rate (breaths per minute) */
  respiratoryRate: {
    min: 12,
    max: 20,
  },

  /** Oxygen saturation (percent) */
  oxygenSaturation: {
    min: 95,
    max: 100,
  },

  /** BMI (body mass index) */
  bmi: {
    min: 18.5,
    max: 24.9,
  },
} as const;

// ============================================================================
// COOKIES & STORAGE
// ============================================================================

/**
 * Session cookie name.
 */
export const SESSION_COOKIE_NAME = "medrcm_session_id";

/**
 * Session cookie options.
 */
export const SESSION_COOKIE_OPTIONS = {
  /** HttpOnly flag (not accessible via JavaScript) */
  httpOnly: true,

  /** Secure flag (HTTPS only) */
  secure: true,

  /** SameSite policy (CSRF protection) */
  sameSite: "Strict" as const,

  /** Session cookie (deleted when browser closes) */
  maxAge: undefined,

  /** Cookie path */
  path: "/",
} as const;

/**
 * Execution token storage key (browser localStorage).
 */
export const EXECUTION_TOKEN_STORAGE_KEY = "medrcm_execution_token";

/**
 * Approval request ID storage key (browser localStorage).
 */
export const APPROVAL_REQUEST_ID_STORAGE_KEY = "medrcm_approval_request_id";

/**
 * User preferences storage key (browser localStorage).
 */
export const USER_PREFERENCES_STORAGE_KEY = "medrcm_user_preferences";

/**
 * Theme preference storage key (browser localStorage).
 */
export const THEME_STORAGE_KEY = "medrcm_theme";

// ============================================================================
// ERROR MESSAGES
// ============================================================================

/**
 * User-facing error messages.
 * Backend maps error codes to these messages before sending to frontend.
 */
export const ERROR_MESSAGES = {
  // Authentication errors
  UNAUTHORIZED: "You are not authorized to perform this action.",
  SESSION_EXPIRED: "Your session has expired. Please log in again.",
  INVALID_CREDENTIALS: "Invalid email or password.",
  ACCOUNT_LOCKED: "Your account has been locked due to multiple failed login attempts.",
  MFA_REQUIRED: "Multi-factor authentication is required.",
  INVALID_MFA_CODE: "Invalid authentication code.",

  // Governance errors
  GOVERNANCE_DENIED: "This action requires approval before execution.",
  MISSING_EXECUTION_TOKEN: "Execution token is required for this action.",
  INVALID_EXECUTION_TOKEN: "The provided execution token is invalid or expired.",
  PAYLOAD_MISMATCH: "The request payload does not match the approved request.",
  APPROVAL_PENDING: "Your request is pending approval. Please wait.",
  APPROVAL_EXPIRED: "Your approval request has expired.",

  // Data errors
  NOT_FOUND: "The requested resource was not found.",
  DUPLICATE_ENTRY: "A record with this identifier already exists.",
  INVALID_INPUT: "One or more input fields are invalid.",
  CONSTRAINT_VIOLATION: "This action violates data integrity constraints.",

  // HIPAA errors
  PHI_ACCESS_DENIED: "You do not have permission to access this patient information.",
  RECORD_LOCKED: "This record is locked and cannot be modified.",
  CONSENT_REQUIRED: "The patient must provide consent before this action.",

  // Appointment errors
  APPOINTMENT_CONFLICT: "The requested time slot is not available.",
  APPOINTMENT_PAST: "Cannot modify appointments in the past.",
  APPOINTMENT_CANCELLED: "This appointment has been cancelled.",
  INSUFFICIENT_NOTICE: "Appointment cancellations require 24 hours notice.",

  // Prescription errors
  PRESCRIPTION_EXPIRED: "This prescription has expired.",
  NO_REFILLS: "This prescription has no remaining refills.",
  PRESCRIPTION_INVALID: "This prescription is no longer valid.",

  // Server errors
  INTERNAL_SERVER_ERROR: "An unexpected error occurred. Please try again.",
  SERVICE_UNAVAILABLE: "The service is temporarily unavailable. Please try again later.",
  TIMEOUT: "The request took too long. Please try again.",

  // Rate limiting
  RATE_LIMIT_EXCEEDED: "Too many requests. Please wait a few moments before trying again.",

  // Telehealth errors
  TELEHEALTH_NOT_AVAILABLE: "Telehealth is not available for this appointment.",
  TELEHEALTH_FAILED: "Failed to establish telehealth connection.",
  TELEHEALTH_SESSION_EXPIRED: "The telehealth session has expired.",
} as const;

/**
 * Error codes and their HTTP status codes.
 */
export const ERROR_CODES = {
  // Client errors (4xx)
  BAD_REQUEST: { code: "BAD_REQUEST", status: 400 },
  UNAUTHORIZED: { code: "UNAUTHORIZED", status: 401 },
  FORBIDDEN: { code: "FORBIDDEN", status: 403 },
  NOT_FOUND: { code: "NOT_FOUND", status: 404 },
  CONFLICT: { code: "CONFLICT", status: 409 },
  UNPROCESSABLE_ENTITY: { code: "UNPROCESSABLE_ENTITY", status: 422 },
  RATE_LIMIT_EXCEEDED: { code: "RATE_LIMIT_EXCEEDED", status: 429 },

  // Server errors (5xx)
  INTERNAL_SERVER_ERROR: { code: "INTERNAL_SERVER_ERROR", status: 500 },
  SERVICE_UNAVAILABLE: { code: "SERVICE_UNAVAILABLE", status: 503 },
} as const;

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/**
 * Default pagination limit (number of items per page).
 */
export const DEFAULT_PAGE_LIMIT = 20;

/**
 * Maximum pagination limit to prevent abuse.
 */
export const MAX_PAGE_LIMIT = 100;

/**
 * Default sort order for list endpoints.
 */
export const DEFAULT_SORT_ORDER = "desc" as const;

/**
 * Maximum file upload size (bytes).
 * 10 MB.
 */
export const MAX_FILE_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Allowed file types for medical records/uploads.
 */
export const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/tiff",
  "application/dicom", // DICOM medical imaging format
  "video/mp4",
  "video/quicktime",
] as const;

/**
 * Maximum number of items in a single batch operation.
 */
export const MAX_BATCH_SIZE = 100;

/**
 * Chat/message history limit (maximum number of messages to fetch at once).
 */
export const MESSAGE_HISTORY_LIMIT = 50;

// ============================================================================
// FEATURE FLAGS
// ============================================================================

/**
 * Feature flags for gradual rollout and A/B testing.
 */
export const FEATURE_FLAGS = {
  /** Enable AI-powered clinical decision support */
  AI_CLINICAL_SUPPORT: "ai_clinical_support",

  /** Enable advanced analytics dashboard */
  ADVANCED_ANALYTICS: "advanced_analytics",

  /** Enable telemedicine capabilities */
  TELEHEALTH: "telehealth",

  /** Enable prescription e-signing */
  E_SIGNATURE: "e_signature",

  /** Enable insurance verification integration */
  INSURANCE_VERIFICATION: "insurance_verification",

  /** Enable mobile app */
  MOBILE_APP: "mobile_app",

  /** Enable governance execution tokens (full CPF enforcement) */
  GOVERNANCE_EXECUTION_TOKENS: "governance_execution_tokens",

  /** Enable external MedRCM SDK consultation */
  MEDRCM_SDK_ENABLED: "medrcm_sdk_enabled",
} as const;

// ============================================================================
// LOCALE & FORMATTING
// ============================================================================

/**
 * Default locale for date/number formatting.
 */
export const DEFAULT_LOCALE = "en-US";

/**
 * Currency code.
 */
export const CURRENCY_CODE = "USD";

/**
 * Currency symbol.
 */
export const CURRENCY_SYMBOL = "$";

/**
 * Date format string for UI display.
 */
export const DATE_FORMAT = "MMM DD, YYYY";

/**
 * Time format string for UI display.
 */
export const TIME_FORMAT = "h:mm A";

/**
 * DateTime format string for UI display.
 */
export const DATETIME_FORMAT = "MMM DD, YYYY h:mm A";

// ============================================================================
// REGEX PATTERNS
// ============================================================================

/**
 * Email validation pattern.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Phone number pattern (US format with flexibility).
 */
export const PHONE_PATTERN = /^[\d\s\-()]+$/;

/**
 * NPI (National Provider Identifier) pattern (10 digits).
 */
export const NPI_PATTERN = /^\d{10}$/;

/**
 * Social Security Number pattern (XXX-XX-XXXX).
 */
export const SSN_PATTERN = /^\d{3}-\d{2}-\d{4}$/;

/**
 * Medical license pattern (varies by state, this is a loose pattern).
 */
export const MEDICAL_LICENSE_PATTERN = /^[A-Z]{2}\d{6,8}$/;

/**
 * ICD-10 code pattern (e.g., "E11.9").
 */
export const ICD10_PATTERN = /^[A-Z]\d{2}\.[\dA-Z]{1,}$/;

/**
 * URL pattern for links in text.
 */
export const URL_PATTERN =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
